import React, { useRef, useState, useEffect } from 'react';
import { EntityCard, EntityCardSize, Stack, Box } from '@/components';
import { ReportSearchModel, ReportCardType, UiConfigurationModel } from '@/types';
import Flicking, { ChangedEvent, NeedPanelEvent } from '@egjs/react-flicking';
import { DIRECTION } from '@egjs/flicking';
import { carouselMotion, carouselItemMotion } from '@/animations';
import tailwindConfig from '@/tailwind.screens';
import ReportCarouselHeading, { ReportHeadingModel } from './ReportCarouselHeading';

interface ReportThreeCardsClickCarouselModel {
  cards: ReportSearchModel[];
  cardType: ReportCardType;
  heading?: ReportHeadingModel;
  uiConfiguration?: UiConfigurationModel;
  onNeedPanel(event: NeedPanelEvent): void;
  onCardClick?: (e: React.MouseEvent, data: ReportSearchModel) => void;
}

const ReportThreeCardsClickCarousel = (data: ReportThreeCardsClickCarouselModel) => {
  const { cards, cardType, onNeedPanel, onCardClick } = data;
  const [initialAnimationComplete, setInitialAnimationComplete] = useState<boolean>(false);
  const [panelsPerView, setPanelsPerView] = useState<number>(-1);
  const [cardSize, setCardSize] = useState<EntityCardSize>(EntityCardSize.SMALL);
  const [nextDisabled, setNextDisabled] = useState<boolean>(false);
  const [prevDisabled, setPrevDisabled] = useState<boolean>(true);

  const carousel = useRef<Flicking>(null);

  useEffect(() => {
    const onResize = () => {
      setPanelsPerView(window.innerWidth >= parseInt(tailwindConfig.screens.lg, 10) ? 3 : -1);

      setCardSize(
        window.innerWidth >= parseInt(tailwindConfig.screens.sm, 10) ? EntityCardSize.LARGE : EntityCardSize.SMALL,
      );
    };
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onFlickingMove = () => {
    if (carousel.current === null) return;
    if (window.innerWidth >= parseInt(tailwindConfig.screens.lg, 10)) {
      const panels = carousel.current.visiblePanels;
      panels.forEach((panel) => {
        const progress = panel.outsetProgress;
        const target = panel.element;

        if (window.innerWidth >= parseInt(tailwindConfig.screens.lg, 10)) {
          target.style.transform = `scale(${1 - Math.abs(progress * 0.4) + 0.2
            }) translateX(${Math.abs(progress * 35)}%)`;
          target.style.zIndex = 10 - Math.floor(progress * 10) + '';

          if (progress < -0.2 || progress > 0.8) {
            target.style.opacity = '0';
          } else {
            target.style.transformOrigin = `${100 - Math.abs(progress * 2.9 * 50)}% 50%`;
            target.style.opacity = '1';
          }
        } else {
          target.style.transform = '';
        }
      });
    }
  };

  const onCarouselChanged = ({ currentTarget, direction }: ChangedEvent) => {
    if (!currentTarget.camera.atEdge) {
      setPrevDisabled(false);
      setNextDisabled(false);
    } else {
      if (direction === DIRECTION.NEXT) {
        setPrevDisabled(false);
        setNextDisabled(true);
      } else {
        setPrevDisabled(true);
        setNextDisabled(false);
      }
    }
  };

  return (
    <Stack
      animation={initialAnimationComplete ? { ...carouselMotion, animate: 'active' } : carouselMotion}
      onAnimationComplete={() => setInitialAnimationComplete(true)}
    >
      <ReportCarouselHeading
        heading={data.heading}
        nextDisabled={nextDisabled}
        prevDisabled={prevDisabled}
        onNextClick={() => carousel.current?.next()}
        onPrevClick={() => carousel.current?.prev()}
        className="container"
      />
      <div className="px-5 lg:px-0">
        <Flicking
          className="!overflow-visible pt-[5%] pb-[6%]"
          ref={carousel}
          preventClickOnDrag
          preventDefaultOnDrag
          align="prev"
          bound
          panelsPerView={panelsPerView}
          onNeedPanel={onNeedPanel}
          onMove={onFlickingMove}
          onReady={onFlickingMove}
          onChanged={onCarouselChanged}
        >
          {cards.map((card, index) => (
            <div key={index} className="mx-2 transition-opacity sm:mx-2">
              <Box animation={carouselItemMotion}>
                <EntityCard
                  data={card}
                  cardType={cardType}
                  size={cardSize}
                  className={panelsPerView > 0 && !card.isLoading ? '!h-full !w-full' : ''}
                  onClick={onCardClick}
                />
              </Box>
            </div>
          ))}
        </Flicking>
      </div>
    </Stack>
  );
};

export default ReportThreeCardsClickCarousel;
